import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, ArrowLeft, Sparkles, Send } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { SERVICES } from "../data/services";

const BUDGETS = ["Just exploring", "Small project", "Mid-sized build", "Substantial scope", "Strategic / enterprise"];
const TIMELINES = ["ASAP", "1–2 weeks", "2–4 weeks", "1–3 months", "3+ months"];

export default function QuoteRequest() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    service_type: "",
    project_title: "",
    description: "",
    budget_range: "",
    timeline: "",
    contact_name: user && user !== false ? user.name : "",
    contact_email: user && user !== false ? user.email : "",
    contact_phone: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Pre-fill contact fields once AuthContext finishes loading the user
  useEffect(() => {
    if (user && user !== false) {
      setData((d) => ({
        ...d,
        contact_name: d.contact_name || user.name || "",
        contact_email: d.contact_email || user.email || "",
      }));
    }
  }, [user]);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async () => {
    setError(""); setSubmitting(true);
    try {
      await api.post("/quotes", data);
      setSuccess(true);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  const canNext = () => {
    if (step === 1) return !!data.service_type;
    if (step === 2) return data.project_title.length >= 2 && data.description.length >= 10;
    if (step === 3) return !!data.budget_range && !!data.timeline;
    if (step === 4) return data.contact_name && data.contact_email;
    return true;
  };

  return (
    <div className="min-h-screen bg-[#F8F8F6] text-[#0E0E1A]">
      <Navbar />
      <section className="pt-36 pb-24 relative">
        <div className="absolute top-20 right-1/4 w-[400px] h-[400px] bg-cyan-500/10 blur-[120px] rounded-full" />
        <div className="mx-auto max-w-3xl px-6 lg:px-10 relative">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF] mb-3">/ start a project</p>
          <h1 className="font-display font-black text-4xl lg:text-6xl tracking-tight">
            Tell us your <span className="text-gradient-cyan">vision</span>.
          </h1>
          <p className="text-[#5A5A72] mt-4 max-w-xl">
            Four short steps. Your request goes straight to Xion's secure inbox — never shared, encrypted in transit.
          </p>

          {/* Progress */}
          <div className="mt-12 flex items-center gap-2">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="flex-1">
                <div className={`h-1 rounded-full transition-all duration-500 ${n <= step ? "bg-gradient-to-r from-cyan-400 to-teal-400" : "bg-white/5"}`} />
                <div className={`mt-2 text-[10px] font-mono uppercase tracking-[0.2em] ${n <= step ? "text-[#00D4FF]" : "text-[#6B7280]"}`}>
                  Step {n}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 glass-card rounded-2xl p-7 lg:p-10 min-h-[420px] relative">
            <AnimatePresence mode="wait">
              {success ? (
                <motion.div
                  key="success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                  className="absolute inset-0 grid place-items-center text-center p-10"
                  data-testid="quote-success"
                >
                  <div>
                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-tr from-cyan-400 to-teal-400 grid place-items-center glow-cyan">
                      <Check className="w-8 h-8 text-black" />
                    </div>
                    <h2 className="font-display font-black text-3xl">Request sent.</h2>
                    <p className="text-[#5A5A72] mt-3 max-w-md mx-auto">
                      We've routed your brief to Xion's encrypted inbox. Expect a reply within 24 hours.
                    </p>
                    <div className="mt-8 flex gap-3 justify-center">
                      <button onClick={() => navigate("/")} className="btn-ghost px-5 py-2.5 rounded-lg text-sm">Back home</button>
                      {user && user !== false && (
                        <button onClick={() => navigate("/dashboard")} data-testid="quote-success-dashboard" className="btn-primary px-5 py-2.5 rounded-lg text-sm font-semibold">View in dashboard</button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  {step === 1 && (
                    <div>
                      <h3 className="font-display font-bold text-2xl mb-2">What can we build for you?</h3>
                      <p className="text-[#5A5A72] text-sm mb-6">Pick the service that fits best — you can always discuss more later.</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {SERVICES.map((s) => (
                          <button
                            key={s.id}
                            data-testid={`quote-service-${s.id}`}
                            onClick={() => update("service_type", s.name)}
                            className={`text-left p-4 rounded-xl border transition-all ${data.service_type === s.name ? "border-[#00D4FF]/60 bg-[#00D4FF]/5 glow-cyan" : "border-black/[0.06] bg-white hover:border-white/15"}`}
                          >
                            <p className="font-display font-semibold text-sm">{s.name}</p>
                            <p className="text-[11px] text-[#6B7280] mt-1">{s.short}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {step === 2 && (
                    <div className="space-y-5">
                      <div>
                        <h3 className="font-display font-bold text-2xl mb-2">The brief.</h3>
                        <p className="text-[#5A5A72] text-sm">A working title and what you're trying to achieve.</p>
                      </div>
                      <div>
                        <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Project title</label>
                        <input
                          value={data.project_title} onChange={(e) => update("project_title", e.target.value)}
                          data-testid="quote-title-input"
                          className="mt-2 w-full px-4 py-3.5 rounded-lg bg-white border border-black/[0.08] focus:border-[#00D4FF]/60 focus:ring-2 focus:ring-cyan-400/20 outline-none"
                          placeholder="e.g. Series A Pitch Deck"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Project details</label>
                        <textarea
                          rows={6}
                          value={data.description} onChange={(e) => update("description", e.target.value)}
                          data-testid="quote-description-input"
                          className="mt-2 w-full px-4 py-3.5 rounded-lg bg-white border border-black/[0.08] focus:border-[#00D4FF]/60 focus:ring-2 focus:ring-cyan-400/20 outline-none resize-none"
                          placeholder="Tell us about your audience, brand, and what success looks like..."
                        />
                      </div>
                    </div>
                  )}
                  {step === 3 && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-display font-bold text-2xl mb-2">Budget & timeline.</h3>
                        <p className="text-[#5A5A72] text-sm">Honest ranges help us scope the perfect plan.</p>
                      </div>
                      <div>
                        <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72] mb-3">Budget range</p>
                        <div className="flex flex-wrap gap-2">
                          {BUDGETS.map((b) => (
                            <button
                              key={b} data-testid={`quote-budget-${b.replace(/\s/g, "")}`}
                              onClick={() => update("budget_range", b)}
                              className={`px-4 py-2.5 rounded-full text-sm border transition ${data.budget_range === b ? "border-[#00D4FF]/60 bg-[#00D4FF]/10 text-[#00D4FF]" : "border-black/[0.08] text-[#4B5563] hover:border-white/20"}`}
                            >{b}</button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72] mb-3">Timeline</p>
                        <div className="flex flex-wrap gap-2">
                          {TIMELINES.map((t) => (
                            <button
                              key={t} data-testid={`quote-timeline-${t.replace(/\s/g, "")}`}
                              onClick={() => update("timeline", t)}
                              className={`px-4 py-2.5 rounded-full text-sm border transition ${data.timeline === t ? "border-[#00D4FF]/60 bg-[#00D4FF]/10 text-[#00D4FF]" : "border-black/[0.08] text-[#4B5563] hover:border-white/20"}`}
                            >{t}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  {step === 4 && (
                    <div className="space-y-5">
                      <div>
                        <h3 className="font-display font-bold text-2xl mb-2">How do we reach you?</h3>
                        <p className="text-[#5A5A72] text-sm">We'll respond within 24 hours.</p>
                      </div>
                      <div>
                        <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Full name</label>
                        <input
                          value={data.contact_name} onChange={(e) => update("contact_name", e.target.value)}
                          data-testid="quote-name-input"
                          className="mt-2 w-full px-4 py-3.5 rounded-lg bg-white border border-black/[0.08] focus:border-[#00D4FF]/60 outline-none"
                          placeholder="Your name"
                        />
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Email</label>
                          <input
                            type="email" value={data.contact_email} onChange={(e) => update("contact_email", e.target.value)}
                            data-testid="quote-email-input"
                            className="mt-2 w-full px-4 py-3.5 rounded-lg bg-white border border-black/[0.08] focus:border-[#00D4FF]/60 outline-none"
                            placeholder="you@studio.com"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Phone (optional)</label>
                          <input
                            value={data.contact_phone} onChange={(e) => update("contact_phone", e.target.value)}
                            data-testid="quote-phone-input"
                            className="mt-2 w-full px-4 py-3.5 rounded-lg bg-white border border-black/[0.08] focus:border-[#00D4FF]/60 outline-none"
                            placeholder="+1 555 ..."
                          />
                        </div>
                      </div>
                      {error && <div data-testid="quote-error" className="text-sm text-red-400 bg-red-500/5 border border-red-500/20 rounded-lg px-4 py-3">{error}</div>}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {!success && (
              <div className="mt-10 flex justify-between">
                <button
                  onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}
                  data-testid="quote-back-btn"
                  className="btn-ghost px-5 py-2.5 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-30"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                {step < 4 ? (
                  <button
                    onClick={() => canNext() && setStep((s) => s + 1)} disabled={!canNext()}
                    data-testid="quote-next-btn"
                    className="btn-primary px-5 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 disabled:opacity-40"
                  >
                    Next <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={submit} disabled={!canNext() || submitting}
                    data-testid="quote-submit-btn"
                    className="btn-primary px-6 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-40"
                  >
                    {submitting ? "Sending..." : <>Send to Xion <Send className="w-4 h-4" /></>}
                  </button>
                )}
              </div>
            )}
          </div>

          <p className="mt-6 text-xs text-[#6B7280] flex items-center gap-2">
            <Sparkles className="w-3 h-3 text-[#00D4FF]" />
            Your message is encrypted in transit and stored privately. Only Xion's team can read it.
          </p>

          {/* Direct contact alternative */}
          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            <a
              href="tel:+919902155712"
              data-testid="quote-direct-phone"
              className="group flex items-center gap-3 p-4 rounded-2xl bg-white border border-black/[0.08] hover:border-[#00D4FF]/40 hover:bg-[#00D4FF]/5 transition"
            >
              <div className="w-10 h-10 rounded-lg bg-[#00D4FF]/10 border border-[#00D4FF]/30 grid place-items-center">
                <svg className="w-4 h-4 text-[#00D4FF]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#00D4FF]">Call / WhatsApp</p>
                <p className="font-display font-bold text-sm mt-0.5">+91 99021 55712</p>
              </div>
            </a>
            <a
              href="mailto:baligarvedant@gmail.com?subject=Xion%20Project%20Enquiry"
              data-testid="quote-direct-email"
              className="group flex items-center gap-3 p-4 rounded-2xl bg-white border border-black/[0.08] hover:border-violet-400/40 hover:bg-violet-400/5 transition"
            >
              <div className="w-10 h-10 rounded-lg bg-violet-400/10 border border-violet-400/30 grid place-items-center">
                <svg className="w-4 h-4 text-violet-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-violet-300">Email Vedant</p>
                <p className="font-display font-bold text-sm mt-0.5 break-all">baligarvedant@gmail.com</p>
              </div>
            </a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
